// A breadcrumb longer than this many segments is elided from the LEFT (the
// segments nearest the document say the most) behind a leading '…'.
const DOCUMENT_PATH_MAX_SEGMENTS = 4;

/**
 * A document's ancestor breadcrumb, from the flat tree `listTree` returns.
 *
 * Its own module because two surfaces render the same trail: the command
 * palette (item subtitle + search keywords) and the document editor's header
 * breadcrumbs. One walk, one elision rule, one cycle guard — the two must
 * never disagree about what a document's path is.
 *
 * `byId` maps node id -> { id, name, type, parentId } for every node in the
 * document's scope (folders AND documents — documents can nest under
 * documents).
 */

/**
 * The shared ancestor walk: `node`'s ancestors as NODES, root-first.
 * Cycle-guarded with a seen set: a corrupt parent loop (bad write, hand-edited
 * store) must truncate the trail, not hang the caller. Both public shapes
 * below are views over this one walk.
 */
function walkAncestorNodes(node, byId) {
  const ancestors = [];
  const seen = new Set([node?.id]);
  let cur = node?.parentId != null ? byId.get(node.parentId) : null;
  while (cur) {
    if (seen.has(cur.id)) break;
    seen.add(cur.id);
    ancestors.unshift(cur);
    cur = cur.parentId != null ? byId.get(cur.parentId) : null;
  }
  return ancestors;
}

/** The display fallback for a nameless tree node — shared so the palette path
 *  and the editor breadcrumbs can never label the same node differently. */
export function documentNodeLabel(node) {
  return node?.name || (node?.type === 'folder' ? 'Untitled folder' : 'Untitled');
}

/**
 * THE elision rule, in one place: more than DOCUMENT_PATH_MAX_SEGMENTS
 * entries keep only the last MAX (nearest the document) and report
 * `elided: true` so the caller renders a leading '…'. Works on any array
 * (names for the palette path, nodes for the breadcrumbs).
 */
export function capAncestors(items) {
  if (items.length > DOCUMENT_PATH_MAX_SEGMENTS) {
    return { elided: true, items: items.slice(items.length - DOCUMENT_PATH_MAX_SEGMENTS) };
  }
  return { elided: false, items };
}

/**
 * `node`'s ancestors as nodes ({ id, type, name, … } — whatever `byId` holds),
 * root-first, uncapped. The editor breadcrumbs consume this (they need ids as
 * click targets, and apply `capAncestors` themselves at render time).
 */
export function documentAncestors(node, byId) {
  return walkAncestorNodes(node, byId);
}

/**
 * The palette's string view of the same walk. Returns:
 *   - `path`:     display string, ancestor names root-first joined ' › ',
 *                 capped at DOCUMENT_PATH_MAX_SEGMENTS with a leading '…'.
 *                 '' for a root-level document.
 *   - `segments`: ALL ancestor names, uncapped — search keywords, so a deep
 *                 document is still findable by an elided ancestor's name.
 */
export function documentTreePath(node, byId) {
  const segments = walkAncestorNodes(node, byId).map(documentNodeLabel);
  const { elided, items } = capAncestors(segments);
  const capped = elided ? ['…', ...items] : items;
  return { path: capped.join(' › '), segments };
}
